/**
 * AuditList - Execute, review and manage audits
 *
 * Features:
 *   - Start new audit from published template
 *   - List in-progress and completed audits with filters
 *   - Mobile-first audit runner with task-level responses
 *   - Each task: compliance status, comment, photo upload
 *   - Non-conformance/observation auto-creates finding
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus, Search, ChevronRight, ChevronDown, CheckCircle2,
  AlertTriangle, Camera, MessageSquare, XCircle, Eye, Send, Play,
  ArrowLeft, Save, FileText, CalendarDays, ClipboardCheck,
  X, Check, Minus, Shield, Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Badge } from '@shared/components/ui/badge';
import { Textarea } from '@shared/components/ui/textarea';
import { Label } from '@shared/components/ui/label';
import { Progress } from '@shared/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@shared/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@shared/components/ui/dialog';
import { toast } from '@shared/components/ui/use-toast';
import { supabase } from '@shared/supabaseClient';
import { ENV } from '@shared/config/env';

const STATUS_CONFIG = {
  draft:     { label: 'Draft',      color: 'bg-gray-500/10 text-gray-700 border-gray-300', icon: FileText },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/10 text-amber-700 border-amber-300', icon: Play },
  submitted: { label: 'Submitted',  color: 'bg-green-500/10 text-green-700 border-green-300', icon: CheckCircle2 },
  locked:    { label: 'Locked',     color: 'bg-blue-500/10 text-blue-700 border-blue-300', icon: Shield },
};

const RESPONSE_OPTIONS = [
  { value: 'compliant',        label: 'Compliant',        icon: Check, color: 'bg-green-500 hover:bg-green-600 text-white' },
  { value: 'non_conformity',   label: 'Non-Conformity',   icon: XCircle, color: 'bg-red-500 hover:bg-red-600 text-white' },
  { value: 'at_risk',          label: 'At Risk',           icon: Eye, color: 'bg-amber-500 hover:bg-amber-600 text-white' },
  { value: 'not_applicable',   label: 'N/A',              icon: Minus, color: 'bg-gray-400 hover:bg-gray-500 text-white' },
];

// ─── Start Audit Dialog ─────────────────────────────────────────────
function StartAuditDialog({ open, onClose, user, onStarted }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [auditTitle, setAuditTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (open) loadTemplates();
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('audit_template_versions')
      .select(`
        id, version_label, template_id,
        audit_templates ( id, template_name, template_code, audit_type )
      `)
      .eq('status', 'published')
      .eq('is_current_version', true)
      .order('created_at', { ascending: false });
    setTemplates(data || []);
    setLoading(false);
  };

  const handleStart = async () => {
    if (!selectedTemplate) {
      toast({ title: 'Select a template', variant: 'destructive' });
      return;
    }
    try {
      setStarting(true);
      const template = templates.find(t => t.id === selectedTemplate);
      const title = auditTitle || `${template.audit_templates.template_name} - ${new Date().toLocaleDateString('en-AU')}`;

      // Create audit
      const { data: audit, error } = await supabase
        .from('audits')
        .insert({
          template_version_id: selectedTemplate,
          audit_title: title,
          audit_type: template.audit_templates.audit_type,
          status: 'draft',
          auditor_id: user.id,
          auditor_name: user.full_name || user.email || 'Unknown',
          depot_id: user.depot_id,
          audit_date: new Date().toISOString().split('T')[0],
          start_time: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      toast({ title: 'Audit Started', description: title });
      onStarted(audit);
    } catch (error) {
      console.error('Error starting audit:', error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Start New Audit</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Select Template *</Label>
            {loading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading templates...</div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4">No published templates available. Create and publish a template first.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {templates.map(t => (
                  <div
                    key={t.id}
                    onClick={() => setSelectedTemplate(t.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedTemplate === t.id ? 'border-amber-500 bg-amber-500/5' : 'border-border hover:border-amber-500/30'
                    }`}
                  >
                    <div className="font-medium text-sm">{t.audit_templates.template_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t.audit_templates.template_code} · {t.version_label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Audit Title (optional)</Label>
            <Input
              placeholder="Auto-generated from template name + date"
              value={auditTitle}
              onChange={e => setAuditTitle(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleStart} disabled={starting || !selectedTemplate}>
            {starting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Start Audit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Audit Runner (Field Execution + Read-Only Viewer) ──────────────
function AuditRunner({ audit, user, onBack, readOnly = false }) {
  const [sections, setSections] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [commentingItem, setCommentingItem] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [photoDialogItem, setPhotoDialogItem] = useState(null);
  const [photoMode, setPhotoMode] = useState('choose'); // 'choose' | 'camera' | 'file'
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  // Assignee picker state
  const [assignDialog, setAssignDialog] = useState(null); // { itemId, responseValue, savedResponse, item }
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [assignDueDate, setAssignDueDate] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    loadAuditData();
    loadAllUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit.id]);

  const STAFF_ROLES = ['admin', 'depot_manager', 'approver', 'employee'];

  const loadAllUsers = async () => {
    const { data } = await supabase
      .from('users')
      .select('id, first_name, surname, email, role')
      .in('role', STAFF_ROLES)
      .order('first_name');

    let users = (data || []).map(u => ({
      ...u,
      full_name: [u.first_name, u.surname].filter(Boolean).join(' ') || null,
    }));

    // Ensure current user is always in the list (for testing / self-assign)
    const currentName = [user?.first_name, user?.surname].filter(Boolean).join(' ') || user?.full_name || 'Current User';
    if (user?.id && !users.find(u => u.id === user.id)) {
      users = [{ id: user.id, full_name: currentName, email: user.email, role: user.role || 'admin' }, ...users];
    }

    setAllUsers(users);
  };

  // Stop camera stream when dialog closes
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
  }, [cameraStream]);

  useEffect(() => {
    if (!photoDialogItem) {
      stopCamera();
      setPhotoMode('choose');
      setCapturedImage(null);
      setCameraError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoDialogItem]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      setPhotoMode('camera');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      setCameraStream(stream);
      // Attach to video element after a tick so ref is mounted
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 100);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.name === 'NotAllowedError'
        ? 'Camera permission denied. Please allow camera access in your browser settings.'
        : err.name === 'NotFoundError'
        ? 'No camera found on this device.'
        : `Camera error: ${err.message}`);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(dataUrl);
    stopCamera();
  };

  const useCapturedPhoto = async () => {
    if (!capturedImage) return;
    // Convert data URL to File
    const res = await fetch(capturedImage);
    const blob = await res.blob();
    const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
    handlePhotoUpload(photoDialogItem, file);
    setCapturedImage(null);
    setPhotoMode('choose');
  };

  const loadAuditData = async () => {
    try {
      setLoading(true);

      // Load sections + items for the template version
      const { data: sectionData } = await supabase
        .from('audit_template_sections')
        .select(`
          *, audit_template_items (*)
        `)
        .eq('version_id', audit.template_version_id)
        .order('section_order');

      // Load existing responses
      const { data: responseData } = await supabase
        .from('audit_responses')
        .select('*')
        .eq('audit_id', audit.id);

      // Map responses by template_item_id
      const responseMap = {};
      (responseData || []).forEach(r => {
        responseMap[r.template_item_id] = r;
      });

      // Sort items within each section
      const sorted = (sectionData || []).map(s => ({
        ...s,
        audit_template_items: (s.audit_template_items || []).sort((a, b) => a.item_order - b.item_order),
      }));

      setSections(sorted);
      setResponses(responseMap);
      if (sorted.length > 0) setExpandedSection(sorted[0].id);
    } catch (error) {
      console.error('Error loading audit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (itemId, responseValue) => {
    const existing = responses[itemId];
    try {
      let savedResponse;

      if (existing?.id) {
        const { data, error } = await supabase
          .from('audit_responses')
          .update({
            response_value: responseValue,
            answered_at: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .select()
          .single();
        if (error) throw error;
        savedResponse = data;
      } else {
        const { data, error } = await supabase
          .from('audit_responses')
          .insert({
            audit_id: audit.id,
            template_item_id: itemId,
            response_value: responseValue,
            answered_by: user.id,
          })
          .select()
          .single();
        if (error) throw error;
        savedResponse = data;
      }

      setResponses(prev => ({ ...prev, [itemId]: savedResponse }));

      // For NC/at_risk — open assignee dialog instead of auto-creating
      if (responseValue === 'non_conformity' || responseValue === 'at_risk') {
        const item = sections.flatMap(s => s.audit_template_items).find(i => i.id === itemId);
        const defaultDue = new Date();
        defaultDue.setDate(defaultDue.getDate() + (responseValue === 'non_conformity' ? 7 : 14));
        setAssignDueDate(defaultDue.toISOString().split('T')[0]);
        setAssignNotes('');
        setSelectedAssignee(null);
        setUserSearch('');
        setAssignDialog({ itemId, responseValue, savedResponse, item });
      }
    } catch (error) {
      console.error('Error saving response:', error);
      toast({ title: 'Error', description: 'Failed to save response', variant: 'destructive' });
    }
  };

  const handleSaveComment = async (itemId) => {
    if (!commentText.trim()) return;
    const existing = responses[itemId];
    try {
      if (existing?.id) {
        const { data } = await supabase
          .from('audit_responses')
          .update({ notes: commentText })
          .eq('id', existing.id)
          .select()
          .single();
        setResponses(prev => ({ ...prev, [itemId]: data }));
      }
      setCommentingItem(null);
      setCommentText('');
      toast({ title: 'Comment Saved' });
    } catch (error) {
      console.error('Error saving comment:', error);
    }
  };

  const handleAssignFinding = async () => {
    if (!assignDialog || !selectedAssignee) {
      toast({ title: 'Select Assignee', description: 'Please select a person to assign the action to', variant: 'destructive' });
      return;
    }
    try {
      setAssignSaving(true);
      const { itemId, responseValue, savedResponse, item } = assignDialog;
      const isNC = responseValue === 'non_conformity';
      const findingTitle = `${item?.item_text || 'Item'} — ${isNC ? 'Non-Conformity' : 'At Risk'}`;

      // 1. Create the finding
      const { data: finding, error: findingErr } = await supabase
        .from('audit_findings')
        .insert({
          audit_id: audit.id,
          response_id: savedResponse.id,
          template_item_id: itemId,
          finding_type: isNC ? 'non_conformity' : 'observation',
          severity: isNC ? 'high' : 'medium',
          finding_title: findingTitle,
          finding_description: `${findingTitle} identified during audit`,
          status: 'action_assigned',
          identified_by: user.id,
        })
        .select()
        .single();

      if (findingErr) throw findingErr;

      // 2. Create the corrective action assigned to selected user
      const { error: actionErr } = await supabase
        .from('audit_actions')
        .insert({
          finding_id: finding.id,
          audit_id: audit.id,
          action_type: isNC ? 'corrective_action' : 'improvement_action',
          action_title: `Action: ${findingTitle}`,
          action_description: assignNotes || `Corrective action required for: ${findingTitle}`,
          assigned_to: selectedAssignee.id,
          assigned_by: user.id,
          due_date: assignDueDate,
          priority: isNC ? 'high' : 'medium',
          status: 'assigned',
          requires_verification: isNC,
        });

      if (actionErr) throw actionErr;

      // Fire-and-forget: notify assignee via email
      try {
        const { data: authData } = await supabase.auth.getSession();
        fetch(ENV.SUPABASE.edgeFn('notify-action-assigned'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authData.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            assigned_to_email: selectedAssignee.email,
            assigned_to_name: selectedAssignee.full_name || selectedAssignee.email,
            assigned_by_name: user.full_name || user.email || 'A team member',
            finding_title: findingTitle,
            finding_type: isNC ? 'non_conformity' : 'observation',
            severity: isNC ? 'high' : 'medium',
            action_description: assignNotes || `Corrective action required for: ${findingTitle}`,
            due_date: assignDueDate,
            audit_reference: audit.audit_reference || '',
          }),
        }).catch(err => console.warn('Notification email failed (non-blocking):', err));
      } catch (notifyErr) {
        console.warn('Could not send notification email:', notifyErr);
      }

      toast({
        title: 'Finding & Action Created',
        description: `Assigned to ${selectedAssignee.full_name || selectedAssignee.email}`,
      });
      setAssignDialog(null);
    } catch (error) {
      console.error('Error creating finding/action:', error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setAssignSaving(false);
    }
  };

  const handlePhotoUpload = async (itemId, file) => {
    const existing = responses[itemId];
    if (!existing?.id) {
      toast({ title: 'Respond first', description: 'Select a response before uploading photos', variant: 'destructive' });
      return;
    }
    try {
      const ext = file.name.split('.').pop();
      const path = `audit-evidence/${audit.id}/${itemId}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('audit-evidence').upload(path, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('audit-evidence').getPublicUrl(path);

      await supabase.from('audit_attachments').insert({
        audit_id: audit.id,
        response_id: existing.id,
        file_url: publicUrl,
        file_name: file.name,
        mime_type: file.type,
        file_size: file.size,
        attachment_type: 'evidence_photo',
        uploaded_by: user.id,
      });

      toast({ title: 'Photo Uploaded' });
      setPhotoDialogItem(null);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({ title: 'Upload Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleSubmitAudit = async () => {
    const allItems = sections.flatMap(s => s.audit_template_items);
    const answered = allItems.filter(i => responses[i.id]).length;

    if (answered < allItems.length) {
      const unanswered = allItems.length - answered;
      if (!confirm(`${unanswered} item${unanswered !== 1 ? 's' : ''} unanswered. Submit anyway?`)) return;
    }

    try {
      setSaving(true);

      // Calculate score
      let totalScore = 0;
      let maxScore = 0;
      allItems.forEach(item => {
        const response = responses[item.id];
        maxScore += (item.max_score || 10);
        if (response?.response_value === 'compliant') totalScore += (item.max_score || 10);
        else if (response?.response_value === 'at_risk') totalScore += (item.max_score || 10) * 0.5;
      });

      const scorePercentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

      await supabase.from('audits').update({
        status: 'submitted',
        submitted_at: new Date().toISOString(),
        submitted_by: user.id,
        end_time: new Date().toISOString(),
        total_score: totalScore,
        max_possible_score: maxScore,
        score_percentage: scorePercentage,
        total_items: allItems.length,
        compliant_count: Object.values(responses).filter(r => r.response_value === 'compliant').length,
        non_conformity_count: Object.values(responses).filter(r => r.response_value === 'non_conformity').length,
        at_risk_count: Object.values(responses).filter(r => r.response_value === 'at_risk').length,
        na_count: Object.values(responses).filter(r => r.response_value === 'not_applicable').length,
      }).eq('id', audit.id);

      toast({ title: 'Audit Submitted', description: `Score: ${scorePercentage}%` });
      onBack();
    } catch (error) {
      console.error('Error submitting:', error);
      toast({ title: 'Error', description: 'Failed to submit', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Progress calculation
  const allItems = sections.flatMap(s => s.audit_template_items);
  const answeredCount = allItems.filter(i => responses[i.id]).length;
  const progress = allItems.length > 0 ? Math.round((answeredCount / allItems.length) * 100) : 0;
  const compliantCount = Object.values(responses).filter(r => r.response_value === 'compliant').length;
  const ncCount = Object.values(responses).filter(r => r.response_value === 'non_conformity').length;
  const riskCount = Object.values(responses).filter(r => r.response_value === 'at_risk').length;
  const naCount = Object.values(responses).filter(r => r.response_value === 'not_applicable').length;

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-28 rounded-xl bg-muted/30 animate-pulse" />
        <div className="h-16 rounded-lg bg-muted/20 animate-pulse" />
        {[1,2,3].map(i => <div key={i} className="h-32 rounded-lg border animate-pulse bg-muted/10" />)}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50/50 dark:bg-background">
      {/* ── Enterprise Header ── */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-card/95 backdrop-blur-md border-b shadow-sm">
        <div className="px-5 py-4">
          {/* Top Row: Nav + Title + Submit */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={onBack}
                className="flex items-center justify-center h-9 w-9 rounded-lg border bg-background hover:bg-muted transition-colors shrink-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <div className="min-w-0">
                <h1 className="font-bold text-base leading-tight truncate">{audit.audit_title}</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  {audit.audit_reference && (
                    <span className="font-mono text-xs text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                      {audit.audit_reference}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {audit.audit_date ? new Date(audit.audit_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                  </span>
                </div>
              </div>
            </div>
            {readOnly ? (
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-300 px-3 py-1.5 text-xs font-semibold shrink-0">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                {audit.status === 'locked' ? 'Locked' : 'Submitted'}
                {audit.score_percentage != null && ` · ${audit.score_percentage}%`}
              </Badge>
            ) : (
              <Button
                onClick={handleSubmitAudit}
                disabled={saving}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm font-semibold px-5 shrink-0"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Submit Audit
              </Button>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap tabular-nums">
              {answeredCount}/{allItems.length}
            </span>
          </div>
        </div>

        {/* Stats Strip */}
        <div className="flex border-t divide-x bg-muted/20">
          {[
            { label: 'Progress', value: `${progress}%`, color: 'text-amber-600' },
            { label: 'Compliant', value: compliantCount, color: 'text-emerald-600' },
            { label: 'NC', value: ncCount, color: 'text-red-600' },
            { label: 'At Risk', value: riskCount, color: 'text-amber-600' },
            { label: 'N/A', value: naCount, color: 'text-slate-500' },
          ].map(stat => (
            <div key={stat.label} className="flex-1 py-2 text-center">
              <div className={`text-sm font-bold tabular-nums ${stat.color}`}>{stat.value}</div>
              <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Section Content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 sm:p-5 space-y-4 max-w-3xl mx-auto">
          {sections.map((section, sIdx) => {
            const sectionAnswered = section.audit_template_items.filter(i => responses[i.id]).length;
            const sectionTotal = section.audit_template_items.length;
            const sectionComplete = sectionAnswered === sectionTotal && sectionTotal > 0;
            const isExpanded = expandedSection === section.id;

            return (
              <div
                key={section.id}
                className={`rounded-xl border bg-white dark:bg-card shadow-sm overflow-hidden transition-all ${
                  isExpanded ? 'ring-1 ring-amber-400/50 shadow-md' : ''
                }`}
              >
                {/* Section Header */}
                <button
                  className="w-full flex items-center gap-3 px-5 py-4 hover:bg-muted/30 transition-colors text-left"
                  onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                >
                  <div className={`flex items-center justify-center h-8 w-8 rounded-lg text-xs font-bold shrink-0 ${
                    sectionComplete
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                  }`}>
                    {sectionComplete ? <CheckCircle2 className="h-4 w-4" /> : sIdx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm leading-tight truncate">{section.section_title}</h3>
                    {section.section_description && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{section.section_description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      sectionComplete
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                    }`}>
                      {sectionAnswered}/{sectionTotal}
                    </span>
                    <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                      isExpanded ? 'rotate-90' : ''
                    }`} />
                  </div>
                </button>

                {/* Section Items */}
                {isExpanded && (
                  <div className="border-t">
                    <div className="p-4 sm:p-5 space-y-3">
                      {section.audit_template_items.map((item, idx) => {
                        const response = responses[item.id];
                        const isNC = response?.response_value === 'non_conformity';
                        const isObs = response?.response_value === 'at_risk';
                        const isCompliant = response?.response_value === 'compliant';
                        const isNA = response?.response_value === 'not_applicable';

                        return (
                          <div
                            key={item.id}
                            className={`rounded-xl border-2 p-4 sm:p-5 transition-all duration-200 ${
                              isNC ? 'border-red-200 bg-red-50/60 dark:bg-red-950/10 dark:border-red-800/40' :
                              isObs ? 'border-amber-200 bg-amber-50/60 dark:bg-amber-950/10 dark:border-amber-800/40' :
                              isCompliant ? 'border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/10 dark:border-emerald-800/40' :
                              isNA ? 'border-slate-200 bg-slate-50/40 dark:bg-slate-800/10 dark:border-slate-700/40' :
                              'border-slate-200 bg-white dark:bg-card dark:border-slate-700'
                            }`}
                          >
                            {/* Item Header */}
                            <div className="flex items-start gap-3">
                              <div className={`flex items-center justify-center h-7 w-7 rounded-full text-[11px] font-bold shrink-0 mt-0.5 ${
                                isNC ? 'bg-red-200 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                                isObs ? 'bg-amber-200 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' :
                                isCompliant ? 'bg-emerald-200 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' :
                                isNA ? 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300' :
                                'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                              }`}>
                                {item.item_code || `${idx + 1}`}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold leading-snug">{item.item_text}</p>
                                {item.guidance_notes && (
                                  <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50/80 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30">
                                    <Eye className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
                                    <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">{item.guidance_notes}</p>
                                  </div>
                                )}
                                {item.requirement_reference && (
                                  <span className="inline-flex items-center mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                    {item.requirement_reference}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Response Buttons — 2x2 Grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                              {RESPONSE_OPTIONS.map(opt => {
                                const Icon = opt.icon;
                                const isSelected = response?.response_value === opt.value;
                                return (
                                  <button
                                    key={opt.value}
                                    onClick={() => !readOnly && handleResponse(item.id, opt.value)}
                                    disabled={readOnly}
                                    className={`
                                      flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-xs font-semibold
                                      transition-all duration-200 border-2 focus:outline-none focus:ring-2 focus:ring-offset-1
                                      ${readOnly ? 'cursor-default' : ''}
                                      ${isSelected
                                        ? opt.value === 'compliant' ? 'bg-emerald-500 text-white border-emerald-500 shadow-md shadow-emerald-200 dark:shadow-emerald-900/30 ring-2 ring-emerald-300 ring-offset-1' :
                                          opt.value === 'non_conformity' ? 'bg-red-500 text-white border-red-500 shadow-md shadow-red-200 dark:shadow-red-900/30 ring-2 ring-red-300 ring-offset-1' :
                                          opt.value === 'at_risk' ? 'bg-amber-500 text-white border-amber-500 shadow-md shadow-amber-200 dark:shadow-amber-900/30 ring-2 ring-amber-300 ring-offset-1' :
                                          'bg-slate-500 text-white border-slate-500 shadow-md shadow-slate-200 dark:shadow-slate-900/30 ring-2 ring-slate-300 ring-offset-1'
                                        : readOnly
                                          ? 'bg-slate-50 dark:bg-card border-slate-100 dark:border-slate-800 text-slate-400 dark:text-slate-500'
                                          : 'bg-white dark:bg-card border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-400 hover:shadow-sm'
                                      }
                                    `}
                                  >
                                    <Icon className={`h-4 w-4 ${isSelected ? '' : 'opacity-60'}`} />
                                    <span className="leading-none">{opt.label}</span>
                                  </button>
                                );
                              })}
                            </div>

                            {/* Action Bar */}
                            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-dashed border-slate-200 dark:border-slate-700">
                              {response?.notes ? (
                                <button
                                  onClick={() => { setCommentingItem(commentingItem === item.id ? null : item.id); setCommentText(response.notes || ''); }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/40"
                                >
                                  <MessageSquare className="h-3.5 w-3.5" />
                                  View Comment
                                </button>
                              ) : !readOnly ? (
                                <button
                                  onClick={() => { setCommentingItem(item.id); setCommentText(''); }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors bg-white dark:bg-card text-muted-foreground border-slate-200 dark:border-slate-700 hover:border-slate-400 hover:text-foreground"
                                >
                                  <MessageSquare className="h-3.5 w-3.5" />
                                  Add Comment
                                </button>
                              ) : null}
                              {!readOnly && (
                                <button
                                  onClick={() => setPhotoDialogItem(item.id)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border bg-white dark:bg-card text-muted-foreground border-slate-200 dark:border-slate-700 hover:border-slate-400 hover:text-foreground transition-colors"
                                >
                                  <Camera className="h-3.5 w-3.5" />
                                  Add Photo
                                </button>
                              )}
                              {(isNC || isObs) && (
                                <div className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                  isNC
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                }`}>
                                  <AlertTriangle className="h-3.5 w-3.5" />
                                  Finding Raised
                                </div>
                              )}
                            </div>

                            {/* Inline Comment Editor / Read-Only Comment */}
                            {commentingItem === item.id && (
                              <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2">
                                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                  Observation Notes
                                </label>
                                {readOnly ? (
                                  <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border p-3 text-sm text-foreground whitespace-pre-wrap">
                                    {response?.notes || 'No notes'}
                                  </div>
                                ) : (
                                  <>
                                    <Textarea
                                      value={commentText}
                                      onChange={e => setCommentText(e.target.value)}
                                      placeholder="Enter observation notes or additional details..."
                                      rows={3}
                                      className="text-sm resize-none"
                                      autoFocus
                                    />
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] text-muted-foreground tabular-nums">{commentText.length} characters</span>
                                      <div className="flex gap-2">
                                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setCommentingItem(null)}>
                                          Cancel
                                        </Button>
                                        <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleSaveComment(item.id)}>
                                          <Save className="h-3 w-3 mr-1.5" /> Save Note
                                        </Button>
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Bottom Spacer */}
          <div className="h-4" />
        </div>
      </div>

      {/* ── Photo Upload Dialog (Webcam + File) ── */}
      <Dialog open={!!photoDialogItem} onOpenChange={o => { if (!o) setPhotoDialogItem(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-muted-foreground" />
              Capture Evidence Photo
            </DialogTitle>
          </DialogHeader>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handlePhotoUpload(photoDialogItem, file);
            }}
          />
          <canvas ref={canvasRef} className="hidden" />

          {/* Mode: Choose */}
          {photoMode === 'choose' && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={startCamera}
                className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-amber-400 hover:bg-amber-50/30 dark:hover:bg-amber-900/10 transition-all cursor-pointer group"
              >
                <div className="h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:bg-amber-100 dark:group-hover:bg-amber-900/30 transition-colors">
                  <Camera className="h-7 w-7 text-slate-400 group-hover:text-amber-600 transition-colors" />
                </div>
                <div className="text-center">
                  <span className="text-sm font-semibold block group-hover:text-foreground text-muted-foreground">
                    Take Photo
                  </span>
                  <span className="text-[10px] text-muted-foreground">Use webcam</span>
                </div>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center gap-3 p-6 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-all cursor-pointer group"
              >
                <div className="h-14 w-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 transition-colors">
                  <FileText className="h-7 w-7 text-slate-400 group-hover:text-blue-600 transition-colors" />
                </div>
                <div className="text-center">
                  <span className="text-sm font-semibold block group-hover:text-foreground text-muted-foreground">
                    Choose File
                  </span>
                  <span className="text-[10px] text-muted-foreground">Browse files</span>
                </div>
              </button>
            </div>
          )}

          {/* Mode: Camera Live */}
          {photoMode === 'camera' && !capturedImage && (
            <div className="space-y-3">
              {cameraError ? (
                <div className="rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/40 p-4 text-center space-y-2">
                  <XCircle className="h-8 w-8 text-red-400 mx-auto" />
                  <p className="text-sm text-red-700 dark:text-red-400">{cameraError}</p>
                  <Button size="sm" variant="outline" onClick={() => { setPhotoMode('choose'); setCameraError(null); }}>
                    Go Back
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                    />
                    {/* Viewfinder corners */}
                    <div className="absolute inset-4 pointer-events-none">
                      <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white/70 rounded-tl" />
                      <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white/70 rounded-tr" />
                      <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white/70 rounded-bl" />
                      <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white/70 rounded-br" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => { stopCamera(); setPhotoMode('choose'); }}
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <Button
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                      onClick={capturePhoto}
                    >
                      <Camera className="h-4 w-4 mr-2" /> Capture
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Mode: Review captured photo */}
          {photoMode === 'camera' && capturedImage && (
            <div className="space-y-3">
              <div className="relative rounded-xl overflow-hidden bg-black">
                <img src={capturedImage} alt="Captured" className="w-full" />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setCapturedImage(null); startCamera(); }}
                >
                  <Camera className="h-4 w-4 mr-2" /> Retake
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={useCapturedPhoto}
                >
                  <Check className="h-4 w-4 mr-2" /> Use Photo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Assign Action Dialog (NC / At Risk) ── */}
      <Dialog open={!!assignDialog} onOpenChange={o => { if (!o) setAssignDialog(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${assignDialog?.responseValue === 'non_conformity' ? 'text-red-500' : 'text-amber-500'}`} />
              {assignDialog?.responseValue === 'non_conformity' ? 'Non-Conformity — Assign Action' : 'At Risk — Assign Action'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Finding summary */}
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border p-3">
              <p className="text-sm font-medium">{assignDialog?.item?.item_text}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  assignDialog?.responseValue === 'non_conformity'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                }`}>
                  {assignDialog?.responseValue === 'non_conformity' ? 'NON-CONFORMITY' : 'AT RISK'}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Severity: {assignDialog?.responseValue === 'non_conformity' ? 'High' : 'Medium'}
                </span>
              </div>
            </div>

            {/* Assign To — searchable user picker */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Assign Action To *
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={userSearch}
                  onChange={e => { setUserSearch(e.target.value); setSelectedAssignee(null); }}
                  className="pl-9"
                />
              </div>
              <div className="max-h-40 overflow-y-auto border rounded-lg divide-y">
                {(userSearch.trim()
                  ? allUsers.filter(u =>
                      (u.full_name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
                      (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
                    )
                  : allUsers
                ).length === 0 ? (
                  <div className="p-3 text-center text-xs text-muted-foreground">
                    {userSearch.trim() ? 'No users match your search' : 'No registered users found'}
                  </div>
                ) : (
                  (userSearch.trim()
                    ? allUsers.filter(u =>
                        (u.full_name || '').toLowerCase().includes(userSearch.toLowerCase()) ||
                        (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
                      )
                    : allUsers
                  ).map(u => (
                    <button
                      key={u.id}
                      onClick={() => { setSelectedAssignee(u); setUserSearch(u.full_name || u.email); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 ${
                        selectedAssignee?.id === u.id ? 'bg-amber-50 dark:bg-amber-900/10 border-l-2 border-l-amber-500' : ''
                      }`}
                    >
                      <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300 shrink-0">
                        {(u.full_name || u.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{u.full_name || 'Unnamed'}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{u.email}</p>
                      </div>
                      {u.role && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-muted-foreground shrink-0 capitalize">
                          {u.role}
                        </span>
                      )}
                      {selectedAssignee?.id === u.id && (
                        <CheckCircle2 className="h-4 w-4 text-amber-600 shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Due Date */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Due Date *
              </Label>
              <Input
                type="date"
                value={assignDueDate}
                onChange={e => setAssignDueDate(e.target.value)}
              />
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Action Notes (optional)
              </Label>
              <Textarea
                value={assignNotes}
                onChange={e => setAssignNotes(e.target.value)}
                placeholder="Describe the corrective action required..."
                rows={2}
                className="text-sm resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
            <Button
              onClick={handleAssignFinding}
              disabled={assignSaving || !selectedAssignee || !assignDueDate}
              className={assignDialog?.responseValue === 'non_conformity'
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'
              }
            >
              {assignSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Create Finding & Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Main Audit List ────────────────────────────────────────────────
export default function AuditList({ user, onNavigate: _onNavigate }) {
  const [audits, setAudits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [runningAudit, setRunningAudit] = useState(null);

  useEffect(() => { loadAudits(); }, []);

  const loadAudits = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('audits')
        .select(`
          *,
          audit_template_versions (
            version_label,
            audit_templates ( template_name, template_code )
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setAudits(data || []);
    } catch (error) {
      console.error('Error loading audits:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const [viewMode, setViewMode] = useState(false); // true = read-only viewer

  if (runningAudit) {
    return (
      <AuditRunner
        audit={runningAudit}
        user={user}
        readOnly={viewMode}
        onBack={() => { setRunningAudit(null); setViewMode(false); loadAudits(); }}
      />
    );
  }

  const filtered = audits.filter(a => {
    const matchesSearch = !search || 
      a.audit_title?.toLowerCase().includes(search.toLowerCase()) ||
      a.audit_reference?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Audits</h2>
          <p className="text-sm text-muted-foreground">{audits.length} audit{audits.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setShowStartDialog(true)}>
          <Plus className="h-4 w-4 mr-2" /> New Audit
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search audits..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Audit cards */}
      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <Card key={i} className="h-24 animate-pulse bg-muted/20" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <ClipboardCheck className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">No audits found</p>
          <Button variant="outline" className="mt-4" onClick={() => setShowStartDialog(true)}>
            <Plus className="h-4 w-4 mr-2" /> Start your first audit
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(audit => {
            const cfg = STATUS_CONFIG[audit.status] || STATUS_CONFIG.draft;
            const StatusIcon = cfg.icon;
            const templateName = audit.audit_template_versions?.audit_templates?.template_name || 'Unknown';
            const isActive = audit.status === 'in_progress' || audit.status === 'draft';

            return (
              <Card
                key={audit.id}
                className={`cursor-pointer hover:border-amber-500/50 hover:shadow-md transition-all ${
                  isActive ? 'border-l-4 border-l-amber-500' : ''
                }`}
                onClick={() => {
                  if (isActive) {
                    setViewMode(false);
                    setRunningAudit(audit);
                  } else {
                    setViewMode(true);
                    setRunningAudit(audit);
                  }
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${cfg.color}`}>
                      <StatusIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm truncate">{audit.audit_title}</h3>
                        <Badge variant="outline" className={`text-[10px] shrink-0 ${cfg.color}`}>
                          {cfg.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />{templateName}
                        </span>
                        {audit.audit_reference && (
                          <span className="font-mono">{audit.audit_reference}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <CalendarDays className="h-3 w-3" />
                          {new Date(audit.created_at).toLocaleDateString('en-AU')}
                        </span>
                      </div>
                    </div>
                    {audit.score_percentage !== null && audit.score_percentage !== undefined && (
                      <div className="text-right shrink-0">
                        <div className={`text-lg font-bold ${
                          audit.score_percentage >= 85 ? 'text-green-600' :
                          audit.score_percentage >= 70 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {audit.score_percentage}%
                        </div>
                        <div className="text-[10px] text-muted-foreground">Score</div>
                      </div>
                    )}
                    {isActive ? (
                      <Button size="sm" variant="outline" className="shrink-0">
                        <Play className="h-3.5 w-3.5 mr-1" /> Continue
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="shrink-0">
                        <Eye className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                    )}
                  </div>
                    {audit.total_items > 0 && (
                    <div className="mt-3">
                      <Progress
                        value={audit.total_items > 0 ? (audit.compliant_count / audit.total_items) * 100 : 0}
                        className="h-1.5 [&>div]:bg-amber-500"
                      />
                      <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
                        <span>{audit.compliant_count}/{audit.total_items} items</span>
                        {audit.non_conformity_count > 0 && (
                          <span className="text-red-600">{audit.non_conformity_count} NC</span>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <StartAuditDialog
        open={showStartDialog}
        onClose={() => setShowStartDialog(false)}
        user={user}
        onStarted={(audit) => { setShowStartDialog(false); setRunningAudit(audit); }}
      />

      {/* Hidden file input for camera */}
      <input ref={undefined} type="file" accept="image/*" className="hidden" />
    </div>
  );
}
